import React, { useEffect, useState } from 'react';
import { customerApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

const PERIOD_LABELS = { today: 'Heute', week: 'Letzte 7 Tage', last_30_days: 'Letzte 30 Tage' };

// Volle Klassennamen literal halten (nicht per Template-String zusammenbauen) —
// Tailwind erkennt beim Production-Build nur Klassen, die irgendwo im Quellcode
// als vollständiger String auftauchen, sonst werden sie beim Purge entfernt.
const COLOR_CLASSES = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
  orange: 'text-orange-600',
  gray: 'text-gray-600',
};

function StatGroup({ title, data, color = 'gray', hint }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm font-medium text-gray-700">{title}</div>
        {hint && <div className="text-xs text-gray-400">{hint}</div>}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <div key={key}>
            <div className="text-xs text-gray-400">{label}</div>
            <div className={`text-2xl font-bold mt-0.5 ${COLOR_CLASSES[color] || COLOR_CLASSES.gray}`}>
              {data ? data[key] : '–'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopList({ title, items, renderLabel, renderValue }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="text-sm font-medium text-gray-700 mb-3">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-gray-400">Keine Daten in den letzten 30 Tagen.</div>
      ) : (
        <ol className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
                {renderLabel(item)}
              </div>
              <span className="font-semibold text-gray-900 flex-shrink-0">{renderValue(item)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      customerApi.list(),
      customerApi.dashboardStats(),
    ]).then(([c, s]) => {
      setCustomers(c.data.results || c.data);
      setStats(s.data);
    }).finally(() => setLoading(false));
  }, []);

  const counts = {
    total: customers.length,
    active: customers.filter(c => c.status === 'active').length,
    paused: customers.filter(c => c.status === 'paused').length,
    blocked: customers.filter(c => c.status === 'blocked').length,
  };

  if (loading) return <div className="p-6"><LoadingSpinner /></div>;

  return (
    <div className="p-6">
      <img src="/logo-wide.png" alt="IncoMail" className="h-12 object-contain mb-6 block ml-auto" />

      {/* Kunden-Kennzahlen */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Kunden gesamt', value: counts.total, color: 'blue' },
          { label: 'Aktiv', value: counts.active, color: 'green' },
          { label: 'Pausiert', value: counts.paused, color: 'yellow' },
          { label: 'Gesperrt', value: counts.blocked, color: 'red' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="text-sm text-gray-500">{label}</div>
            <div className={`text-3xl font-bold mt-1 text-${color}-600`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Mail- und Sicherheits-Kennzahlen (kundenübergreifend) */}
      <div className="grid grid-cols-2 gap-4">
        <StatGroup title="E-Mails insgesamt" data={stats?.mails} color="blue" />
        <StatGroup title="Davon als Spam erkannt" data={stats?.spam} color="orange" />
        <StatGroup title="Erfolgreiche Anmeldungen" data={stats?.logins_success} color="green" />
        <StatGroup
          title="Fehlgeschlagene Anmeldeversuche"
          data={stats?.logins_failed}
          color="red"
          hint={stats?.logins_failed?.today > 10 ? '⚠️ auffällig viele heute' : undefined}
        />
      </div>

      {/* Top-5-Ranglisten (letzte 30 Tage) */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        <TopList
          title="Top 5 Mail-Empfänger-Konten"
          items={stats?.top_received_accounts}
          renderLabel={(item) => (
            <div className="min-w-0">
              <div className="truncate font-medium text-gray-900">{item.email}</div>
              <div className="truncate text-xs text-gray-400">{item.customer}</div>
            </div>
          )}
          renderValue={(item) => item.count}
        />
        <TopList
          title="Top 5 Mail-Versender-Konten"
          items={stats?.top_sent_accounts}
          renderLabel={(item) => (
            <div className="min-w-0">
              <div className="truncate font-medium text-gray-900">{item.email}</div>
              <div className="truncate text-xs text-gray-400">{item.customer}</div>
            </div>
          )}
          renderValue={(item) => item.count}
        />
        <TopList
          title="Top 5 API-Token-Verbraucher"
          items={stats?.top_token_customers}
          renderLabel={(item) => (
            <div className="min-w-0 truncate font-medium text-gray-900">{item.customer}</div>
          )}
          renderValue={(item) => item.tokens.toLocaleString('de-DE')}
        />
      </div>
    </div>
  );
}
